import { useQuery } from '@tanstack/react-query';
import {
  ROTULO_STATUS_IMPLANTACAO,
  formatarValorMensal,
  type StatusImplantacao,
} from '@reducee/contracts';
import { useState } from 'react';
import { Link } from 'react-router';

import { chamarApi } from '../api/cliente.ts';
import { CabecalhoDePagina, Pilula, data } from '../componentes/Superficie.tsx';

type Cliente = {
  contatoId: string;
  nome: string;
  contratos: number;
  produtos: string[];
  operadoras: string[];
  producaoCentavos: number;
  producaoPagaCentavos: number;
  vidas: number;
  vigenciaInicio: string | null;
  implantacaoPendente: StatusImplantacao | null;
  parcelasVencidas: number;
  documentos: number;
  responsaveis: string[];
};

/**
 * Carteira 360º.
 *
 * Lista quem já é Cliente — Contato com Contrato. A pergunta aqui não é "com
 * quem falei", e sim "o que já vendi e como está sendo sustentado"; por isso
 * cada cartão mistura produção, implantação e cobrança.
 */
export function PaginaClientes(): React.JSX.Element {
  const [busca, definirBusca] = useState('');

  const consulta = useQuery({
    queryKey: ['carteira', busca],
    queryFn: () =>
      chamarApi<Cliente[]>(`/contatos/carteira?busca=${encodeURIComponent(busca.trim())}`),
  });

  const clientes = consulta.data ?? [];
  const inadimplentes = clientes.filter((cliente) => cliente.parcelasVencidas > 0).length;
  const emImplantacao = clientes.filter((cliente) => cliente.implantacaoPendente).length;

  return (
    <section>
      <CabecalhoDePagina
        area="Clientes"
        titulo="Carteira 360º"
        descricao="Cadastro, vendas, contratos, implantação, movimentações e histórico."
        acoes={
          <Link
            to="/importar-carteira"
            className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
          >
            Importar carteira
          </Link>
        }
      />

      <input
        type="search"
        value={busca}
        onChange={(evento) => definirBusca(evento.target.value)}
        placeholder="Filtrar por nome…"
        aria-label="Filtrar clientes por nome"
        className="mt-6 w-full max-w-sm rounded-lg border border-linha px-3 py-2 outline-none focus:border-marinho-claro"
      />

      <div className="mt-4 grid gap-3">
        {clientes.map((cliente) => (
          <Link
            key={cliente.contatoId}
            to={`/contatos/${cliente.contatoId}`}
            data-teste="cliente-da-carteira"
            // `min-w-0` pela mesma razão do Cartão: o piso deste era 764px, e
            // num celular a lista dobrava a largura da tela.
            className="flex min-w-0 items-start justify-between gap-4 rounded-2xl border border-linha bg-superficie p-4.25 shadow-card transition hover:border-marinho-claro"
          >
            <div className="min-w-0">
              <p className="truncate text-base font-[850] text-marinho">{cliente.nome}</p>
              <p className="truncate text-[12px] text-texto-suave">
                {[cliente.operadoras.join(', '), cliente.produtos.join(', ')]
                  .filter(Boolean)
                  .join(' · ')}{' '}
                · {cliente.contratos} registro(s)
              </p>
              <p className="mt-1 text-[11px] text-texto-suave">
                Produção: {formatarValorMensal(cliente.producaoCentavos)} · Vigência:{' '}
                {cliente.vigenciaInicio ? data(cliente.vigenciaInicio) : 'a definir'} · Docs:{' '}
                {cliente.documentos}
              </p>
            </div>

            <div className="shrink-0 space-y-2 text-right">
              {cliente.implantacaoPendente ? (
                <Pilula tom="alerta">
                  {ROTULO_STATUS_IMPLANTACAO[cliente.implantacaoPendente]}
                </Pilula>
              ) : (
                <Pilula tom="sucesso">Ativo</Pilula>
              )}
              {/* Só aparece quando há atraso: é aviso de cobrança, não resumo. */}
              {cliente.parcelasVencidas > 0 ? (
                <div>
                  <Pilula tom="erro">{cliente.parcelasVencidas} parcela(s) vencida(s)</Pilula>
                </div>
              ) : null}
              <div>
                <span className="inline-block rounded-[9px] border border-linha px-3 py-1.5 text-[13px] font-[750]">
                  Abrir 360º
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {clientes.length === 0 ? (
        <p className="mt-6 rounded-xl bg-superficie p-8 text-center text-texto-suave ring-1 ring-linha">
          {consulta.isLoading
            ? 'Carregando…'
            : 'Nenhum cliente na carteira. Um Contato vira Cliente quando a Oportunidade é fechada, ou pela importação da carteira.'}
        </p>
      ) : null}
    </section>
  );
}
